import { BottomButtonFrame } from '@/components/bottom-button'
import {
  CheckoutView,
  CheckoutPaymentMethodsOverlay,
  CheckoutSubmitButton,
} from '@/components/checkout-view'
import { Form } from '@/components/form'
import { TrashIcon } from '@/components/trash-icon'
import { useAppContext } from '@/pages/_app'
import { CHECKOUT_FORM_ID, useCheckout } from '@/services/checkout'
import { useVenueContext } from '@/services/venue'
import { useRef } from 'react'
import { Trans } from 'react-i18next'

export default function CheckoutSidePanel() {
  const { t } = useAppContext()
  const { orderManager, orderTypeHasPaymentMethods } = useVenueContext()!
  const { orderMode, basket } = orderManager
  const checkoutController = useCheckout()
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  return (
    <div
      className={`wrapper w-full pt-5 ${
        basket.size === 0 ? 'pb-5' : ''
      } px-7 flex-shrink-0 hidden sticky h-screen top-0 overflow-x-auto md:block`}
    >
      <style jsx>{`
        .wrapper::-webkit-scrollbar {
          width: 4px;
          background-color: transparent;
        }

        .wrapper::-webkit-scrollbar-thumb {
          background: #dcdfe7;
          border-radius: 0;
        }

        .wrapper::-webkit-scrollbar-track {
          -webkit-box-shadow: none;
        }
      `}</style>
      <div className="h-full" ref={wrapperRef}>
        <Form
          id={CHECKOUT_FORM_ID}
          className="flex flex-col min-h-full bg-[#f8f8f8] dark:bg-[#2F3133] rounded-xl"
        >
          <div className="h-16 flex items-center font-bold text-2xl leading-tight py-4 px-4 dark:text-primaryAccentDark">
            <h1>
              {(() => {
                switch (orderMode.value) {
                  case 'onSite':
                    return t('common:myOrder')
                  case 'delivery':
                    return t('checkout:myDelivery')
                  case 'takeaway':
                    return t('checkout:myTakeaway')
                }
              })()}
            </h1>
            {checkoutController.orderState.type === 'pending' && basket.size > 0 ? (
              <div
                className="ml-auto h-6 flex items-center"
                onClick={() => {
                  return checkoutController.actions.toggleDeleteMode()
                }}
              >
                {checkoutController.productListMode.type === 'delete' ? (
                  <button type="button" className="focus:outline-none text-lg">
                    {t('common:done')}
                  </button>
                ) : (
                  <button type="button" className="focus:outline-none">
                    <TrashIcon color="stroke-primary dark:stroke-primaryDark" />
                  </button>
                )}
              </div>
            ) : null}
          </div>
          {basket.size > 0 ? <CheckoutView controller={checkoutController} /> : <EmptyBasket />}

          <CheckoutPaymentMethodsOverlay
            controller={checkoutController}
            width={() => {
              return wrapperRef.current?.clientWidth ?? 0
            }}
          />

          {basket.size > 0 && orderTypeHasPaymentMethods && (
            <div className="z-40 mt-auto sticky bottom-0">
              <BottomButtonFrame>
                <CheckoutSubmitButton controller={checkoutController} />
              </BottomButtonFrame>
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}

function EmptyBasket() {
  const { t } = useAppContext()
  return (
    <div className="flex flex-grow items-center justify-center">
      <div className="text-lg text-center text-opaqueLight px-4">
        <Trans
          t={t}
          i18nKey="checkoutPanel:yourBasketIsEmpty"
          components={{ p: <p className="my-2" /> }}
        />
      </div>
    </div>
  )
}
